import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import apiClient from '@/lib/api';
import { Monitor, Smartphone, Tablet, MapPin, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SessionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await apiClient.get('/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const terminateSession = async (sessionId) => {
    try {
      await apiClient.delete(`/sessions/${sessionId}`);
      toast.success('Session terminated');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to terminate session');
    }
  };

  const terminateAllSessions = async () => {
    if (!confirm('Are you sure you want to terminate all other sessions?')) return;

    try {
      await apiClient.delete('/sessions');
      toast.success('All other sessions terminated');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to terminate sessions');
    }
  };

  const getDeviceIcon = (deviceInfo) => {
    if (!deviceInfo?.user_agent) return Monitor;
    const ua = deviceInfo.user_agent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return Smartphone;
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return Tablet;
    }
    return Monitor;
  };

  const getDeviceName = (deviceInfo) => {
    if (!deviceInfo?.user_agent) return 'Unknown Device';
    const ua = deviceInfo.user_agent;
    if (ua.includes('Chrome')) return 'Chrome Browser';
    if (ua.includes('Firefox')) return 'Firefox Browser';
    if (ua.includes('Safari')) return 'Safari Browser';
    if (ua.includes('Edge')) return 'Edge Browser';
    return 'Unknown Browser';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-600 dark:text-zinc-400">Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sessions-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Active Sessions</h2>
          <p className="text-zinc-600 dark:text-zinc-400 mt-1">
            Manage devices currently signed in to your account
          </p>
        </div>
        {sessions.length > 1 && (
          <Button
            variant="destructive"
            onClick={terminateAllSessions}
            data-testid="terminate-all-button"
          >
            Terminate All Others
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {sessions.map((session, index) => {
          const DeviceIcon = getDeviceIcon(session.device_info);
          const deviceName = getDeviceName(session.device_info);
          const isExpired = new Date(session.expires_at) < new Date();

          return (
            <Card key={session.session_id} data-testid={`session-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                        <DeviceIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold">{deviceName}</h3>
                        {index === 0 && (
                          <Badge variant="default" data-testid="current-session-badge">
                            Current Session
                          </Badge>
                        )}
                        {session.trusted && (
                          <Badge variant="outline">Trusted</Badge>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
                        {session.ip_address && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="h-3 w-3" />
                            <span>{session.ip_address}</span>
                            {session.location && <span>• {session.location}</span>}
                          </div>
                        )}
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            Created {format(new Date(session.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        {session.last_activity && (
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>
                              Last active {format(new Date(session.last_activity), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                        )}
                        <div className="text-xs">
                          Expires {format(new Date(session.expires_at), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {index !== 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => terminateSession(session.session_id)}
                      data-testid={`terminate-session-${index}`}
                    >
                      Terminate
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {sessions.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Monitor className="h-12 w-12 text-zinc-400 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No active sessions</h3>
            <p className="text-zinc-600 dark:text-zinc-400">
              You don't have any active sessions
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SessionsPage;
